import { useMemo, useState } from "react";
import { IconSearch } from "@tabler/icons-react";
import { toast } from "sonner";
import {
  useEmailTemplatesQuery,
  type EmailTemplate,
} from "../api/queries/emailTemplateQueries";
import { useUpdateEscalationLevelMutation } from "../api/queries/escalationQueries";

interface LinkEmailTemplateModalProps {
  opened: boolean;
  onClose: () => void;
  levelId: number;
  currentTemplateId: number | null;
}

export function LinkEmailTemplateModal({
  opened,
  onClose,
  levelId,
  currentTemplateId,
}: LinkEmailTemplateModalProps) {
  const [search, setSearch] = useState("");
  const { data: templates = [], isLoading } = useEmailTemplatesQuery();
  const updateLevel = useUpdateEscalationLevelMutation();

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return templates;
    return templates.filter(
      (template) =>
        template.name.toLowerCase().includes(query) ||
        template.subject.toLowerCase().includes(query),
    );
  }, [templates, search]);

  const handleSelect = async (template: EmailTemplate) => {
    if (template.id === currentTemplateId) {
      onClose();
      return;
    }
    try {
      await updateLevel.mutateAsync({
        levelId,
        email_template_id: template.id,
      });
      toast.success("Template linked");
      onClose();
    } catch {
      toast.error("Failed to link template");
    }
  };

  if (!opened) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-xl border border-gray-200 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-gray-200 px-5 py-4">
          <h3 className="text-lg font-semibold text-gray-900">Link template</h3>
          <p className="mt-1 text-sm text-gray-500">
            Choose an existing email template for this escalation step.
          </p>
          <div className="relative mt-3">
            <IconSearch
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="search"
              placeholder="Search templates…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
          {isLoading ? (
            <p className="px-2 py-6 text-center text-sm text-gray-500">
              Loading templates…
            </p>
          ) : filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-gray-500">
              No templates found.
            </p>
          ) : (
            <ul className="space-y-1">
              {filtered.map((template) => {
                const selected = template.id === currentTemplateId;
                return (
                  <li key={template.id}>
                    <button
                      type="button"
                      disabled={updateLevel.isPending}
                      onClick={() => void handleSelect(template)}
                      className={`w-full rounded-lg border px-3 py-2.5 text-left transition-colors ${
                        selected
                          ? "border-emerald-300 bg-emerald-50"
                          : "border-transparent hover:border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <p className="font-medium text-gray-900">{template.name}</p>
                      <p className="mt-0.5 truncate text-xs text-gray-500">
                        {template.subject}
                      </p>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex justify-end border-t border-gray-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
